import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from './supabaseClient';
import './AuthForm.css';
import Sidebar from './Sidebar';
import ProfileMenu from './ProfileMenu';
import MobileHamburger from './MobileHamburger';
import MobileMenu from './MobileMenu';
import './Main.css';
import { useNavigate } from 'react-router-dom';
import { 
  FaEye, FaLocationDot, FaUser, FaClock, FaCircleCheck, FaBan, 
  FaEnvelope, FaPhone, FaBuilding, FaUserTie, FaTrophy, FaChartLine, 
  FaCalendarCheck, FaCertificate, FaIdBadge, FaXmark, FaFileLines, 
  FaDownload, FaMapPin, FaCheckCircle, 
  FaHourglassHalf, FaChartBar, FaUsers, FaRoute, 
  FaStopwatch, FaFlag, FaBell,
  FaArrowUp, FaArrowDown, FaEquals, FaCalendarAlt, FaClock as FaClockIcon
} from 'react-icons/fa6';

import { 
  FaHistory, FaExclamationTriangle, FaSearch, FaFilter 
} from 'react-icons/fa';

import { 
  FaRotateRight
} from 'react-icons/fa6';
import Preloader from './Preloader';

function ResponderIncidentTracking() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [activeTab, setActiveTab] = useState('current');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [sidebarNarrow, setSidebarNarrow] = useState(false);
  
  // Current assignments data
  const [currentAssignments, setCurrentAssignments] = useState([]);
  const [responderStatuses, setResponderStatuses] = useState([]);
  
  // Incident history data
  const [incidentHistory, setIncidentHistory] = useState([]);
  const [selectedResponder, setSelectedResponder] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  
  // Performance metrics
  const [performanceMetrics, setPerformanceMetrics] = useState([]);
  const [metricsLoading, setMetricsLoading] = useState(false);
  
  // Filters and search
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [selectedResponderForHistory, setSelectedResponderForHistory] = useState('');
  
  // Real-time updates
  const [lastUpdate, setLastUpdate] = useState(new Date());
  
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Responder Incident Tracking";
    getCurrentUser();
    fetchCurrentAssignments();
    fetchResponderStatuses();
    fetchPerformanceMetrics();
    
    // Set up real-time subscriptions
    setupRealtimeSubscriptions();
  }, []);

  useEffect(() => {
    if (currentUser) {
      fetchCurrentAssignments();
      fetchResponderStatuses();
    }
  }, [currentUser]);

  async function getCurrentUser() {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();
      console.log('User profile:', profile);
      console.log('User role:', profile?.role);
      setCurrentUser(profile);
    }
  }

  function setupRealtimeSubscriptions() {
    // Subscribe to responder availability changes
    const responderChannel = supabase
      .channel('responder_availability_tracking')
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'admin_responder_availability' },
        (payload) => {
          console.log('Responder availability change:', payload);
          fetchResponderStatuses();
          setLastUpdate(new Date());
        }
      )
      .subscribe();

    // Subscribe to incident response changes
    const incidentChannel = supabase
      .channel('incident_responses_tracking')
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'responder_incident_responses' },
        (payload) => {
          console.log('Incident response change:', payload);
          fetchCurrentAssignments();
          setLastUpdate(new Date());
        }
      )
      .subscribe();

    // Subscribe to incident report changes
    const incidentReportChannel = supabase
      .channel('incident_reports_tracking')
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'incident_reports' },
        (payload) => {
          console.log('Incident report change:', payload);
          fetchCurrentAssignments();
          setLastUpdate(new Date());
        }
      )
      .subscribe();

    return () => {
      responderChannel.unsubscribe();
      incidentChannel.unsubscribe();
      incidentReportChannel.unsubscribe();
    };
  }

  async function fetchCurrentAssignments() {
    try {
      // Try using the RPC function first
      const { data: rpcData, error: rpcError } = await supabase
        .rpc('get_current_responder_assignments');

      if (!rpcError && rpcData) {
        setCurrentAssignments(rpcData);
      } else {
        console.log('RPC function failed, using fallback query:', rpcError);
        
        // Fallback to manual query
        const { data, error } = await supabase
          .from('responder_incident_responses')
          .select(`
            *,
            approved_responders!inner(
              id,
              first_name,
              last_name,
              responder_id,
              profession,
              organization,
              mobile_number,
              email
            ),
            incident_reports!inner(
              id,
              type,
              location,
              severity,
              status,
              details,
              created_at
            )
          `)
          .in('status', ['accepted', 'responding', 'on_scene'])
          .order('accepted_at', { ascending: false });

        if (error) throw error;
        
                  // Transform data to match expected format
        const transformedData = data?.map(assignment => ({
          id: assignment.id,
          responder_id: assignment.responder_id,
          availability_status: assignment.status,
          response_started_at: assignment.accepted_at,
          current_incident_id: assignment.incident_id,
          approved_responders: assignment.approved_responders,
          incident_reports: {
            ...assignment.incident_reports,
            description: assignment.incident_reports.details // Map details to description for component compatibility
          }
        })) || [];

        setCurrentAssignments(transformedData);
      }
    } catch (error) {
      console.error('Error fetching current assignments:', error);
      setError('Failed to load current assignments');
    }
  }

  async function fetchResponderStatuses() {
    try {
      const { data, error } = await supabase
        .from('approved_responders')
        .select('*')
        .eq('is_active', true)
        .order('first_name', { ascending: true });

      if (error) throw error;
      setResponderStatuses(data || []);
    } catch (error) {
      console.error('Error fetching responder statuses:', error);
      setError('Failed to load responder statuses');
    } finally {
      setLoading(false);
    }
  }

  async function fetchIncidentHistory(responderId = null) {
    setHistoryLoading(true);
    try {
      let query = supabase
        .from('responder_incident_responses')
        .select(`
          *,
          incident_reports!inner(
            type,
            location,
            severity,
            status,
            created_at
          ),
          approved_responders!inner(
            first_name,
            last_name,
            responder_id,
            profession
          )
        `)
        .order('accepted_at', { ascending: false });

      if (responderId) {
        query = query.eq('responder_id', responderId);
      }

      const { data, error } = await query;
      if (error) throw error;
      setIncidentHistory(data || []);
    } catch (error) {
      console.error('Error fetching incident history:', error);
      setError('Failed to load incident history');
    } finally {
      setHistoryLoading(false);
    }
  }

  async function fetchPerformanceMetrics() {
    setMetricsLoading(true);
    try {
      // Try the RPC function first
      const { data, error } = await supabase
        .rpc('get_responder_performance_metrics');

      if (error) {
        console.log('RPC function failed, using fallback:', error);
        // Fallback to manual query if RPC doesn't exist
        const { data: fallbackData, error: fallbackError } = await supabase
          .from('approved_responders')
          .select(`
            id,
            first_name,
            last_name,
            responder_id,
            profession,
            organization,
            responder_incident_responses!left(
              id,
              accepted_at,
              completed_at,
              incident_reports!left(
                status
              )
            )
          `)
          .eq('is_active', true);

        if (fallbackError) throw fallbackError;
        
        // Calculate metrics manually
        const metrics = fallbackData.map(responder => {
          const responses = responder.responder_incident_responses || [];
          const totalResponses = responses.length;
          const successfulResolutions = responses.filter(r => 
            r.incident_reports?.status === 'resolved'
          ).length;
          
          const avgResponseTime = responses.length > 0 ? 
            responses.reduce((acc, r) => {
              if (r.accepted_at && r.completed_at) {
                const start = new Date(r.accepted_at);
                const end = new Date(r.completed_at);
                return acc + (end - start) / (1000 * 60); // Convert to minutes
              }
              return acc;
            }, 0) / responses.length : 0;

          return {
            ...responder,
            total_responses: totalResponses,
            successful_resolutions: successfulResolutions,
            avg_response_time_minutes: Math.round(avgResponseTime * 100) / 100,
            success_rate: totalResponses > 0 ? (successfulResolutions / totalResponses * 100).toFixed(1) : 0
          };
        });

        setPerformanceMetrics(metrics);
      } else {
        // Convert BIGINT IDs to strings for React keys if needed
        const processedData = data ? data.map(item => ({
          ...item,
          id: item.id?.toString() || item.id, // Convert BIGINT to string for React keys
          responder_id: item.responder_id?.toString() || item.responder_id,
          current_incident_id: item.current_incident_id?.toString() || item.current_incident_id
        })) : [];
        setPerformanceMetrics(processedData);
      }
    } catch (error) {
      console.error('Error fetching performance metrics:', error);
      setError('Failed to load performance metrics');
    } finally {
      setMetricsLoading(false);
    }
  }

  function getStatusColor(status) {
    switch (status) {
      case 'available': return '#10b981';
      case 'responding': return '#f59e0b';
      case 'on_scene': return '#ef4444';
      case 'unavailable': return '#6b7280';
      default: return '#9ca3af';
    }
  }

  function getStatusIcon(status) {
    switch (status) {
      case 'available': return <FaCircleCheck style={{ color: '#10b981' }} />;
      case 'responding': return <FaHourglassHalf style={{ color: '#f59e0b' }} />;
      case 'on_scene': return <FaExclamationTriangle style={{ color: '#ef4444' }} />;
      case 'unavailable': return <FaBan style={{ color: '#6b7280' }} />;
      default: return <FaUser style={{ color: '#9ca3af' }} />;
    }
  }

  function getSeverityColor(severity) {
    switch (severity?.toLowerCase()) {
      case 'critical': return '#ef4444';
      case 'high': return '#f97316';
      case 'medium': return '#f59e0b';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  }

  function formatDate(dateString) {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  function formatDuration(startTime, endTime = null) {
    if (!startTime) return 'N/A';
    
    const start = new Date(startTime);
    const end = endTime ? new Date(endTime) : new Date();
    const diffMs = end - start;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);
    const remainingMins = diffMins % 60;
    
    if (diffHours > 0) {
      return `${diffHours}h ${remainingMins}m`;
    }
    return `${diffMins}m`;
  }

  function calculateResponseTime(acceptedAt, completedAt) {
    if (!acceptedAt) return 'N/A';
    
    const start = new Date(acceptedAt);
    const end = completedAt ? new Date(completedAt) : new Date();
    const diffMs = end - start;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    
    if (diffMins < 60) {
      return `${diffMins} minutes`;
    } else {
      const hours = Math.floor(diffMins / 60);
      const mins = diffMins % 60;
      return `${hours}h ${mins}m`;
    }
  }

  const filteredCurrentAssignments = useMemo(() => {
    return currentAssignments.filter(assignment => {
      const matchesSearch = !searchTerm || 
        assignment.approved_responders?.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        assignment.approved_responders?.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        assignment.approved_responders?.responder_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        assignment.incident_reports?.type?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || assignment.availability_status === statusFilter;
      
      return matchesSearch && matchesStatus;
    });
  }, [currentAssignments, searchTerm, statusFilter]);

  const filteredResponderStatuses = useMemo(() => {
    return responderStatuses.filter(responder => {
      const matchesSearch = !searchTerm || 
        responder.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        responder.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        responder.responder_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        responder.profession?.toLowerCase().includes(searchTerm.toLowerCase());
      
      return matchesSearch;
    });
  }, [responderStatuses, searchTerm]);

  if (loading) return <Preloader text="Loading incident tracking..." />;

  if (!currentUser || (currentUser.role !== 'admin' && currentUser.role !== 'super admin' && currentUser.role !== 'super_admin')) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: '#f4f8f8' }}>
        <Sidebar />
        <div style={{ flex: 1, padding: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff3cd', color: '#856404', border: '1px solid #ffeeba', borderRadius: 8, padding: '32px 48px', fontWeight: 500, fontSize: 20 }}>
            Access denied. You do not have the necessary permissions to view this page.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f4f8f8' }}>
      <Sidebar onNarrowChange={setSidebarNarrow}/>
      <div style={{ flex: 1, padding: 32, width: '100%', overflowX: 'auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
          <div>
            <div className="header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                {/* Mobile Hamburger Menu */}
                <MobileHamburger
                  isOpen={showMobileMenu}
                  onToggle={() => setShowMobileMenu(prev => !prev)}
                />
                <div className="title-text">Responder Incident Tracking</div>
              </div>
            </div>

          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ fontSize: 12, color: '#6b7280', textAlign: 'right' }}>
              <div>Last updated: {lastUpdate.toLocaleTimeString()}</div>
              <div>Live updates enabled</div>
            </div>
            <ProfileMenu />
          </div>
        </div>

        <p style={{ color: '#7f8c8d', marginBottom: '24px' }}>
          Real-time monitoring of emergency responder assignments and performance
        </p>

        {/* Mobile Dropdown Menu */}
        <MobileMenu 
          isOpen={showMobileMenu} 
          onClose={() => setShowMobileMenu(false)} 
        />

        {error && (
          <div style={{ background: '#ffebee', color: '#c62828', padding: 16, borderRadius: 8, marginBottom: 24 }}>
            {error}
          </div>
        )}

        {/* Tab Navigation */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 24, borderBottom: '1px solid #e5e7eb' }}>
          <button
            onClick={() => setActiveTab('current')}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: activeTab === 'current' ? '#08b1c7' : 'transparent',
              color: activeTab === 'current' ? '#fff' : '#6b7280',
              borderRadius: '8px 8px 0 0',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: 14,
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            <FaClock /> Current Assignments
          </button>
          <button
            onClick={() => setActiveTab('status')}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: activeTab === 'status' ? '#08b1c7' : 'transparent',
              color: activeTab === 'status' ? '#fff' : '#6b7280',
              borderRadius: '8px 8px 0 0',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: 14,
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            <FaUsers /> Responder Status
          </button>
          <button
            onClick={() => setActiveTab('history')}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: activeTab === 'history' ? '#08b1c7' : 'transparent',
              color: activeTab === 'history' ? '#fff' : '#6b7280',
              borderRadius: '8px 8px 0 0',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: 14,
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            <FaHistory /> Incident History
          </button>
          <button
            onClick={() => setActiveTab('performance')}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: activeTab === 'performance' ? '#08b1c7' : 'transparent',
              color: activeTab === 'performance' ? '#fff' : '#6b7280',
              borderRadius: '8px 8px 0 0',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: 14,
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            <FaChartBar /> Performance Metrics
          </button>
        </div>

        {/* Search and Filters */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 24, alignItems: 'center' }}>
          <div style={{ display: 'flex', flex: 1, gap: 16, alignItems: 'center' }}>
            <div style={{ position: 'relative', width: '100%', minWidth: 300 }}>
              <FaSearch style={{ 
                position: 'absolute', 
                left: 12, 
                top: '50%', 
                transform: 'translateY(-50%)', 
                color: '#9ca3af' 
              }} />
              <input
                type="text"
                placeholder="Search responders, incidents, or locations..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                style={{ 
                  width: '95%', 
                  padding: '12px 12px 12px 40px', 
                  borderRadius: 8, 
                  border: '1px solid #d1d5db',
                  fontSize: 14
                }}
              />
            </div>
            
            {activeTab === 'current' && (
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                style={{ 
                  flex: 1, 
                  minWidth: 150, 
                  padding: 12, 
                  borderRadius: 8, 
                  border: '1px solid #d1d5db',
                  fontSize: 14
                }}
              >
                <option value="all">All Status</option>
                <option value="responding">Responding</option>
                <option value="on_scene">On Scene</option>
              </select>
            )}

            <button
              onClick={() => {
                fetchCurrentAssignments();
                fetchResponderStatuses();
                fetchPerformanceMetrics();
                setLastUpdate(new Date());
              }}
              style={{
                background: '#08b1c7',
                color: '#fff',
                border: 'none',
                padding: '12px 16px',
                borderRadius: 8,
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
            >
                             <FaRotateRight /> Refresh
            </button>
          </div>
        </div>

        {/* Current Assignments Tab */}
        {activeTab === 'current' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ fontSize: 20, fontWeight: 600, color: '#1f2937', margin: 0 }}>
                Current Incident Assignments ({filteredCurrentAssignments.length})
              </h2>
              <div style={{ fontSize: 14, color: '#6b7280' }}>
                Real-time updates
              </div>
            </div>

            <div style={{ display: 'grid', gap: 16 }}>
              {filteredCurrentAssignments.length === 0 ? (
                <div style={{ 
                  background: '#fff', 
                  padding: 40, 
                  borderRadius: 12, 
                  textAlign: 'center',
                  color: '#6b7280',
                  border: '1px solid #e5e7eb'
                }}>
                  <FaClock style={{ fontSize: 48, marginBottom: 16, color: '#d1d5db' }} />
                  <h3 style={{ margin: '0 0 8px 0', color: '#374151' }}>No Active Assignments</h3>
                  <p style={{ margin: 0, fontSize: 14 }}>
                    No responders are currently assigned to active incidents.
                  </p>
                </div>
              ) : (
                filteredCurrentAssignments.map(assignment => (
                  <div key={assignment.id} style={{
                    background: '#fff',
                    borderRadius: 12,
                    padding: 24,
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        {getStatusIcon(assignment.availability_status)}
                        <div>
                          <h3 style={{ margin: '0 0 4px 0', fontSize: 18, fontWeight: 600, color: '#1f2937' }}>
                            {assignment.approved_responders?.first_name} {assignment.approved_responders?.last_name}
                          </h3>
                          <div style={{ fontSize: 14, color: '#6b7280' }}>
                            {assignment.approved_responders?.responder_id} • {assignment.approved_responders?.profession}
                          </div>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{
                          background: getStatusColor(assignment.availability_status),
                          color: '#fff',
                          padding: '6px 12px',
                          borderRadius: 20,
                          fontSize: 12,
                          fontWeight: 600,
                          textTransform: 'capitalize'
                        }}>
                          {assignment.availability_status.replace('_', ' ')}
                        </div>
                        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                          {formatDuration(assignment.response_started_at)}
                        </div>
                      </div>
                    </div>

                    <div style={{ 
                      background: '#f8fafc', 
                      borderRadius: 8, 
                      padding: 16, 
                      border: '1px solid #e2e8f0',
                      marginBottom: 16
                    }}>
                      <h4 style={{ margin: '0 0 12px 0', fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
                        Incident Details
                      </h4>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                        <div>
                          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Type</div>
                          <div style={{ fontSize: 14, fontWeight: 500, color: '#1f2937' }}>
                            {assignment.incident_reports?.type || 'N/A'}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Severity</div>
                          <div style={{ fontSize: 14, fontWeight: 500, color: getSeverityColor(assignment.incident_reports?.severity) }}>
                            {assignment.incident_reports?.severity || 'N/A'}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Location</div>
                          <div style={{ fontSize: 14, fontWeight: 500, color: '#1f2937' }}>
                            {assignment.incident_reports?.location || 'N/A'}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Status</div>
                          <div style={{ fontSize: 14, fontWeight: 500, color: '#1f2937' }}>
                            {assignment.incident_reports?.status || 'N/A'}
                          </div>
                        </div>
                      </div>
                      {assignment.incident_reports?.description && (
                        <div style={{ marginTop: 12 }}>
                          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Description</div>
                          <div style={{ fontSize: 14, color: '#374151' }}>
                            {assignment.incident_reports.description}
                          </div>
                        </div>
                      )}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#6b7280' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <FaPhone /> {assignment.approved_responders?.mobile_number || 'N/A'}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <FaEnvelope /> {assignment.approved_responders?.email || 'N/A'}
                        </div>
                      </div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>
                        Started: {formatDate(assignment.response_started_at)}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Responder Status Tab */}
        {activeTab === 'status' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ fontSize: 20, fontWeight: 600, color: '#1f2937', margin: 0 }}>
                Responder Status Overview ({filteredResponderStatuses.length})
              </h2>
              <div style={{ fontSize: 14, color: '#6b7280' }}>
                Live status monitoring
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: 16 }}>
              {filteredResponderStatuses.map(responder => (
                <div key={responder.id} style={{
                  background: '#fff',
                  borderRadius: 12,
                  padding: 20,
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                    <div>
                      <h3 style={{ margin: '0 0 4px 0', fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
                        {responder.first_name} {responder.last_name}
                      </h3>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>
                        {responder.responder_id} • {responder.profession}
                      </div>
                      {responder.organization && (
                        <div style={{ fontSize: 12, color: '#6b7280' }}>
                          {responder.organization}
                        </div>
                      )}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      {getStatusIcon(responder.admin_responder_availability?.[0]?.availability_status || 'available')}
                      <div style={{
                        background: getStatusColor(responder.admin_responder_availability?.[0]?.availability_status || 'available'),
                        color: '#fff',
                        padding: '4px 8px',
                        borderRadius: 12,
                        fontSize: 10,
                        fontWeight: 600,
                        textTransform: 'capitalize',
                        marginTop: 4
                      }}>
                        {responder.admin_responder_availability?.[0]?.availability_status || 'available'}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 12, fontSize: 12, color: '#6b7280' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <FaPhone /> {responder.mobile_number || 'N/A'}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <FaEnvelope /> {responder.email || 'N/A'}
                    </div>
                  </div>

                  {responder.admin_responder_availability?.[0]?.current_incident_id && (
                    <div style={{ 
                      background: '#fef3c7', 
                      borderRadius: 8, 
                      padding: 12, 
                      marginTop: 12,
                      border: '1px solid #fbbf24'
                    }}>
                      <div style={{ fontSize: 12, color: '#92400e', marginBottom: 4, fontWeight: 600 }}>
                        Currently Assigned
                      </div>
                      <div style={{ fontSize: 12, color: '#92400e' }}>
                        Incident #{responder.admin_responder_availability[0].current_incident_id}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Incident History Tab */}
        {activeTab === 'history' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ fontSize: 20, fontWeight: 600, color: '#1f2937', margin: 0 }}>
                Incident Response History
              </h2>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <select
                  value={selectedResponderForHistory}
                  onChange={e => {
                    setSelectedResponderForHistory(e.target.value);
                    if (e.target.value) {
                      fetchIncidentHistory(e.target.value);
                    } else {
                      fetchIncidentHistory();
                    }
                  }}
                  style={{ 
                    padding: '8px 12px', 
                    borderRadius: 6, 
                    border: '1px solid #d1d5db',
                    fontSize: 14
                  }}
                >
                  <option value="">All Responders</option>
                  {responderStatuses.map(responder => (
                    <option key={responder.id} value={responder.id}>
                      {responder.first_name} {responder.last_name} ({responder.responder_id})
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => fetchIncidentHistory(selectedResponderForHistory || null)}
                  disabled={historyLoading}
                  style={{
                    background: '#08b1c7',
                    color: '#fff',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: 6,
                    cursor: historyLoading ? 'not-allowed' : 'pointer',
                    fontSize: 14,
                    opacity: historyLoading ? 0.6 : 1
                  }}
                >
                  {historyLoading ? 'Loading...' : 'Load History'}
                </button>
              </div>
            </div>

            {historyLoading ? (
              <Preloader text="Loading incident history..." />
            ) : (
              <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f8f9fa' }}>
                        <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 600, color: '#2c3e50', borderBottom: '1px solid #e9ecef' }}>
                          Responder
                        </th>
                        <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 600, color: '#2c3e50', borderBottom: '1px solid #e9ecef' }}>
                          Incident
                        </th>
                        <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 600, color: '#2c3e50', borderBottom: '1px solid #e9ecef' }}>
                          Response Time
                        </th>
                        <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 600, color: '#2c3e50', borderBottom: '1px solid #e9ecef' }}>
                          Status
                        </th>
                        <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 600, color: '#2c3e50', borderBottom: '1px solid #e9ecef' }}>
                          Date
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {incidentHistory.length === 0 ? (
                        <tr>
                          <td colSpan="5" style={{ padding: 40, textAlign: 'center', color: '#7f8c8d' }}>
                            No incident history found
                          </td>
                        </tr>
                      ) : (
                        incidentHistory.map(incident => (
                          <tr key={incident.id} style={{ borderBottom: '1px solid #f1f3f4' }}>
                            <td style={{ padding: '16px 20px' }}>
                              <div>
                                <div style={{ fontWeight: 600, color: '#2c3e50' }}>
                                  {incident.approved_responders?.first_name} {incident.approved_responders?.last_name}
                                </div>
                                <div style={{ fontSize: 12, color: '#7f8c8d' }}>
                                  {incident.approved_responders?.responder_id} • {incident.approved_responders?.profession}
                                </div>
                              </div>
                            </td>
                            <td style={{ padding: '16px 20px' }}>
                              <div>
                                <div style={{ fontWeight: 600, color: '#2c3e50' }}>
                                  {incident.incident_reports?.type}
                                </div>
                                <div style={{ fontSize: 12, color: '#7f8c8d' }}>
                                  {incident.incident_reports?.location}
                                </div>
                                <div style={{ fontSize: 12, color: getSeverityColor(incident.incident_reports?.severity) }}>
                                  {incident.incident_reports?.severity} severity
                                </div>
                              </div>
                            </td>
                            <td style={{ padding: '16px 20px', fontSize: 14, color: '#34495e' }}>
                              {calculateResponseTime(incident.accepted_at, incident.completed_at)}
                            </td>
                            <td style={{ padding: '16px 20px' }}>
                              <span style={{
                                background: incident.incident_reports?.status === 'resolved' ? '#10b981' : '#f59e0b',
                                color: '#fff',
                                padding: '4px 12px',
                                borderRadius: 20,
                                fontSize: 12,
                                fontWeight: 600,
                                textTransform: 'capitalize'
                              }}>
                                {incident.incident_reports?.status || 'Unknown'}
                              </span>
                            </td>
                            <td style={{ padding: '16px 20px', fontSize: 14, color: '#7f8c8d' }}>
                              {formatDate(incident.accepted_at)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Performance Metrics Tab */}
        {activeTab === 'performance' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ fontSize: 20, fontWeight: 600, color: '#1f2937', margin: 0 }}>
                Responder Performance Metrics
              </h2>
              <div style={{ fontSize: 14, color: '#6b7280' }}>
                Historical performance analysis
              </div>
            </div>

            {metricsLoading ? (
              <Preloader text="Loading performance metrics..." />
            ) : (
              <div style={{ display: 'grid', gap: 16 }}>
                {performanceMetrics.map(responder => (
                  <div key={responder.id} style={{
                    background: '#fff',
                    borderRadius: 12,
                    padding: 24,
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                      <div>
                        <h3 style={{ margin: '0 0 4px 0', fontSize: 18, fontWeight: 600, color: '#1f2937' }}>
                          {responder.first_name} {responder.last_name}
                        </h3>
                        <div style={{ fontSize: 14, color: '#6b7280' }}>
                          {responder.responder_id} • {responder.profession}
                        </div>
                        {responder.organization && (
                          <div style={{ fontSize: 12, color: '#6b7280' }}>
                            {responder.organization}
                          </div>
                        )}
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 24, fontWeight: 'bold', color: '#10b981' }}>
                          {responder.success_rate}%
                        </div>
                        <div style={{ fontSize: 12, color: '#6b7280' }}>
                          Success Rate
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                      <div style={{ 
                        background: '#f8fafc', 
                        borderRadius: 8, 
                        padding: 16, 
                        border: '1px solid #e2e8f0'
                      }}>
                        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Total Responses</div>
                        <div style={{ fontSize: 24, fontWeight: 'bold', color: '#1f2937' }}>
                          {responder.total_responses}
                        </div>
                      </div>
                      
                      <div style={{ 
                        background: '#f8fafc', 
                        borderRadius: 8, 
                        padding: 16, 
                        border: '1px solid #e2e8f0'
                      }}>
                        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Successful Resolutions</div>
                        <div style={{ fontSize: 24, fontWeight: 'bold', color: '#10b981' }}>
                          {responder.successful_resolutions}
                        </div>
                      </div>
                      
                      <div style={{ 
                        background: '#f8fafc', 
                        borderRadius: 8, 
                        padding: 16, 
                        border: '1px solid #e2e8f0'
                      }}>
                        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Avg Response Time</div>
                        <div style={{ fontSize: 24, fontWeight: 'bold', color: '#1f2937' }}>
                          {responder.avg_response_time_minutes} min
                        </div>
                      </div>
                      
                      <div style={{ 
                        background: '#f8fafc', 
                        borderRadius: 8, 
                        padding: 16, 
                        border: '1px solid #e2e8f0'
                      }}>
                        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Performance Trend</div>
                        <div style={{ fontSize: 24, fontWeight: 'bold', color: '#10b981' }}>
                          {responder.success_rate >= 80 ? <FaArrowUp /> : responder.success_rate >= 60 ? <FaEquals /> : <FaArrowDown />}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ResponderIncidentTracking; 